import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { CustomTable } from "../../componentes/table/CustomTable";
import { useExportToExcel } from "../../componentes/table/useExportToExcel";
import Button from "../../componentes/button/button";
import StatusBadge from "../../components/solicitacoes/Shared/StatusBadge";
import FiltroSolicitacao from "../../components/solicitacoes/Shared/FiltroSolicitacao";
import KanbanBoard from "../../components/solicitacoes/Kanban/KanbanBoard";
import DashboardProcessoModal, { possuiDashboard } from "../../components/solicitacoes/Dashboard/DashboardProcessoModal";
import { useSolicitacoes } from "../../hooks/solicitacoes/useSolicitacoes";
import { useFiltrosPersistidos } from "../../hooks/solicitacoes/useFiltrosPersistidos";
import { PROCESSO_REGISTRY, processoInfo, etapaLabel } from "../../utils/solicitacoes/processoRegistry";
import { humanizarEtapa } from "../../utils/solicitacoes/statusConfig";
import { tempoDecorrido } from "../../utils/solicitacoes/formatters";
import { primeiroDiaMesAtualIso, hojeIso } from "../../utils/solicitacoes/datas";
import { navegarComCtrl } from "../../utils/solicitacoes/navegacao";

const CAMPOS_FILTRO_SEM_PERIODO = ["tipoProcesso", "status", "solicitanteId", "responsavelId", "grupoId"];

const FILTROS_INICIAIS = {
    tipoProcesso: null,
    status: null,
    solicitanteId: null,
    responsavelId: null,
    grupoId: null,
    dataInicial: primeiroDiaMesAtualIso(),
    dataFinal: hojeIso(),
};

const PROCESSOS_FILTRO_RAPIDO = Object.entries(PROCESSO_REGISTRY)
    .map(([codigo, info]) => ({ codigo, label: info.label, icone: info.icone }))
    .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));

// Atalho para o filtro de Processo — mesmo campo `tipoProcesso` do dropdown, então os dois ficam sincronizados.
function FiltroRapidoProcesso({ valor, onChange }) {
    const opcoes = [{ codigo: null, label: "Todos", icone: "fa fa-layer-group" }, ...PROCESSOS_FILTRO_RAPIDO];
    return (
        <div className="flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible md:pb-0">
            {opcoes.map(opcao => {
                const ativo = (valor ?? null) === opcao.codigo;
                return (
                    <button
                        key={opcao.codigo ?? "todos"}
                        type="button"
                        onClick={() => onChange(opcao.codigo)}
                        aria-pressed={ativo}
                        className={`shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-medium transition-colors ${ativo
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-card text-foreground border-border hover:bg-muted"}`}
                    >
                        <i className={`${opcao.icone} text-[11px]`} />
                        {opcao.label}
                    </button>
                );
            })}
        </div>
    );
}

const COLUNAS = [
    { key: "numero", label: "Número", width: 140, className: "cursor-pointer" },
    { key: "processoLabel", label: "Processo", filterable: false, className: "cursor-pointer" },
    { key: "solicitanteNome", label: "Solicitante", filterable: false, className: "cursor-pointer" },
    { key: "etapaLabelTxt", label: "Etapa", filterable: true, className: "cursor-pointer" },
    { key: "responsavel", label: "Responsável", filterable: false, className: "cursor-pointer" },
    { key: "status", label: "Situação", render: v => <StatusBadge status={v} />, center: true, filterable: false, className: "cursor-pointer" },
    { key: "tempoEtapaTxt", label: "Tempo na etapa", sortable: false, filterable: false, className: "cursor-pointer" },
    { key: "dataCriacao", label: "Data", format: "date", className: "cursor-pointer" },
];

const COLUNAS_EXPORTACAO = [
    { key: "numero", label: "Número" },
    { key: "processoLabel", label: "Processo" },
    { key: "solicitanteNome", label: "Solicitante" },
    { key: "etapaLabelTxt", label: "Etapa" },
    { key: "responsavel", label: "Responsável" },
    { key: "status", label: "Situação" },
    { key: "prazoStatus", label: "Prazo da etapa" },
    { key: "prazoDataLimite", label: "Data limite da etapa", format: "date" },
    { key: "dataCriacao", label: "Data de criação", format: "date" },
    { key: "dataAtualizacao", label: "Última atualização", format: "date" },
    { key: "dataConclusao", label: "Data de conclusão", format: "date" },
];

export default function TodasSolicitacoes() {
    const navigate = useNavigate();
    const { exportToExcel } = useExportToExcel();
    const [filtros, setFiltros, limparFiltros] = useFiltrosPersistidos(
        "solicitacoes.todas.filtros", FILTROS_INICIAIS, ["dataInicial", "dataFinal"]
    );
    const [dashboardVisivel, setDashboardVisivel] = useState(false);

    function fecharDashboard() {
        setDashboardVisivel(false);
        // O dashboard embutido troca o título da aba ao montar.
        document.title = "MyDash | Todas as Solicitações";
    }

    // Filtros persistidos de antes do período ser obrigatório podem ter vindo sem data — preenche
    // com o padrão assim que detectado, sem esperar o usuário clicar em Pesquisar.
    useEffect(() => {
        if (!filtros.dataInicial || !filtros.dataFinal) {
            setFiltros(prev => ({
                ...prev,
                dataInicial: prev.dataInicial || FILTROS_INICIAIS.dataInicial,
                dataFinal: prev.dataFinal || FILTROS_INICIAIS.dataFinal,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Estado "aplicado": só isso alimenta a busca no backend. Processo/Status seguem reativos
    // (comportamento já existente), mas dataInicial/dataFinal só entram aqui via `pesquisar`, para
    // nunca disparar uma chamada a cada tecla digitada na data.
    const [filtrosAplicados, setFiltrosAplicados] = useState(() => ({
        tipoProcesso: filtros.tipoProcesso,
        status: filtros.status,
        dataInicial: filtros.dataInicial || FILTROS_INICIAIS.dataInicial,
        dataFinal: filtros.dataFinal || FILTROS_INICIAIS.dataFinal,
    }));

    useEffect(() => {
        setFiltrosAplicados(prev => ({ ...prev, tipoProcesso: filtros.tipoProcesso, status: filtros.status }));
    }, [filtros.tipoProcesso, filtros.status]);

    const { solicitacoes, loading } = useSolicitacoes(
        filtrosAplicados.tipoProcesso, filtrosAplicados.status, filtrosAplicados.dataInicial, filtrosAplicados.dataFinal
    );

    useEffect(() => {
        document.title = "MyDash | Todas as Solicitações";
    }, []);

    function pesquisar() {
        if (!filtros.dataInicial || !filtros.dataFinal) {
            toast.error("Informe a data inicial e a data final para realizar a consulta.");
            return;
        }
        if (filtros.dataInicial > filtros.dataFinal) {
            toast.error("A data inicial não pode ser maior que a data final.");
            return;
        }
        setFiltrosAplicados({
            tipoProcesso: filtros.tipoProcesso, status: filtros.status,
            dataInicial: filtros.dataInicial, dataFinal: filtros.dataFinal,
        });
    }

    const solicitacoesFiltradas = useMemo(() => {
        return solicitacoes.filter(s => {
            if (filtros.solicitanteId && s.solicitanteId !== filtros.solicitanteId) return false;
            if (filtros.responsavelId && s.responsavelAtualId !== filtros.responsavelId) return false;
            if (filtros.grupoId && String(s.grupoResponsavelAtualId) !== String(filtros.grupoId)) return false;
            return true;
        });
    }, [solicitacoes, filtros.solicitanteId, filtros.responsavelId, filtros.grupoId]);

    const linhas = useMemo(() => solicitacoesFiltradas.map(s => ({
        ...s,
        processoLabel: processoInfo(s.tipoProcesso)?.label || s.tipoProcesso,
        etapaLabelTxt: s.etapaAtual ? (etapaLabel(s.tipoProcesso, s.etapaAtual) || humanizarEtapa(s.etapaAtual)) : "Encerrada",
        responsavel: s.responsavelAtualNome || s.grupoResponsavelAtualNome || "-",
        tempoEtapaTxt: tempoDecorrido(s.dataAtualizacao),
        prazoStatus: s.prazoEtapa?.status || "",
        prazoDataLimite: s.prazoEtapa?.dataLimite || null,
        prazoHoras: s.prazoEtapa?.prazoHoras ?? null,
    })), [solicitacoesFiltradas]);

    function exportar() {
        if (!linhas.length) {
            toast.info("Não há solicitações para exportar.");
            return;
        }
        exportToExcel(linhas, COLUNAS_EXPORTACAO, "solicitacoes");
    }

    const temFiltroAtivo = CAMPOS_FILTRO_SEM_PERIODO.some(campo => !!filtros[campo]);

    const modoKanban = !!filtros.tipoProcesso;
    const etapasDoProcesso = modoKanban ? processoInfo(filtros.tipoProcesso)?.etapas || [] : [];

    return (
        <main className="px-4 py-6">
            <div className="mx-auto flex flex-col gap-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <h1 className="font-display text-2xl font-bold text-foreground">Todas as Solicitações</h1>
                    <div className="flex items-center gap-2 flex-wrap">
                        {possuiDashboard(filtros.tipoProcesso) && (
                            <Button variant="outline" label="Ver Dashboard" iconLeft="fa fa-chart-pie" onClick={() => setDashboardVisivel(true)} />
                        )}
                        <Button variant="outline" label="Exportar" iconLeft="fa fa-file-export" onClick={exportar} />
                        <Button variant="blue" label="Nova Solicitação" iconLeft="fa fa-plus" onClick={() => navigate("/solicitacoes/nova")} />
                    </div>
                </div>

                <FiltroRapidoProcesso valor={filtros.tipoProcesso} onChange={tipoProcesso => setFiltros(prev => ({ ...prev, tipoProcesso }))} />

                <div className="bg-card border border-border rounded-lg shadow-card p-4">
                    <div className="flex items-end gap-3 flex-wrap mb-4 pb-4 border-b border-border">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">
                                Data Inicial <span className="text-destructive">*</span>
                            </label>
                            <input
                                type="date"
                                value={filtros.dataInicial ?? ""}
                                onChange={e => setFiltros({ ...filtros, dataInicial: e.target.value })}
                                className="h-9 px-3 text-sm border border-input rounded-lg bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-sm font-medium text-foreground/80">
                                Data Final <span className="text-destructive">*</span>
                            </label>
                            <input
                                type="date"
                                value={filtros.dataFinal ?? ""}
                                onChange={e => setFiltros({ ...filtros, dataFinal: e.target.value })}
                                className="h-9 px-3 text-sm border border-input rounded-lg bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                            />
                        </div>
                        <Button variant="blue" label="Pesquisar" iconLeft="fa fa-search" loading={loading} onClick={pesquisar} />
                    </div>

                    <div className="flex items-center justify-between mb-3">
                        <h2 className="text-sm font-semibold text-foreground">Filtros</h2>
                        {temFiltroAtivo && (
                            <Button variant="outline" label="Limpar filtros" iconLeft="fa fa-eraser" onClick={limparFiltros} />
                        )}
                    </div>
                    <FiltroSolicitacao value={filtros} onChange={setFiltros} campos={CAMPOS_FILTRO_SEM_PERIODO} />
                    {modoKanban && (
                        <p className="text-xs text-muted-foreground mt-3">
                            <i className="fa fa-info-circle mr-1" />
                            Exibindo em quadro Kanban por etapa (filtro de processo ativo).
                        </p>
                    )}
                </div>

                {modoKanban ? (
                    <KanbanBoard etapas={etapasDoProcesso} solicitacoes={solicitacoesFiltradas} />
                ) : (
                    <div className="bg-card border border-border rounded-lg shadow-card ">
                        <CustomTable
                            data={linhas}
                            columns={COLUNAS}
                            loading={loading}
                            showHeader={false}
                            className="text-xs cursor-pointer"
                            pagination
                            pageSize={20}
                            onRowSelect={(row, e) => navegarComCtrl(navigate, `/solicitacoes/${row.id}`, e)}
                        />
                    </div>
                )}
            </div>

            <DashboardProcessoModal tipoProcesso={filtros.tipoProcesso} visible={dashboardVisivel} onFechar={fecharDashboard} />
        </main>
    );
}
